/**
 * Plan and COI exports (POST /v1/exports/plan, /v1/exports/coi).
 *
 * The request carries what's on screen, so exports work for guest drafts,
 * saved plans, and shared views alike. The response is a file; we save it
 * via a temporary object URL. PDF maps use the same background map the
 * person has selected on screen (streets, satellite, or none).
 */
import { ApiError, apiBaseUrl, fetchWithAuth } from './api';
import { useBasemapStore } from '../stores/basemap';

export type ExportFormat = 'csv' | 'equivalency' | 'shapefile' | 'pdf';

export const EXPORT_FORMATS: ReadonlyArray<{ format: ExportFormat; label: string; ext: string }> = [
  { format: 'pdf', label: 'PDF report', ext: 'pdf' },
  { format: 'csv', label: 'CSV summary', ext: 'csv' },
  { format: 'equivalency', label: 'Block equivalency file', ext: 'csv' },
  { format: 'shapefile', label: 'Shapefile', ext: 'zip' },
];

export interface PlanExportInput {
  name: string;
  districtCount: number;
  /** GEOID -> 0-based district index (editor convention). */
  assignments: Record<string, number>;
  labels?: string[];
  colors?: string[];
  comments?: string | null;
}

export interface CoiExportInput {
  title: string;
  narrative: string;
  interests?: string | null;
  polygon: Record<string, unknown> | null;
}

function filenameFrom(response: Response, fallback: string): string {
  const header = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1] ?? fallback;
}

async function download(path: string, body: unknown, fallback: string): Promise<void> {
  const response = await fetchWithAuth(`${apiBaseUrl()}${path}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let message = 'The export failed.';
    try {
      const data = (await response.json()) as { detail?: unknown };
      if (typeof data.detail === 'string') message = data.detail;
    } catch {
      // keep the generic message
    }
    throw new ApiError(response.status, `http_${response.status}`, message);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filenameFrom(response, fallback);
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function exportPlan(format: ExportFormat, input: PlanExportInput): Promise<void> {
  const oneBased: Record<string, number> = {};
  for (const [geoid, index] of Object.entries(input.assignments)) oneBased[geoid] = index + 1;
  const ext = EXPORT_FORMATS.find((f) => f.format === format)?.ext ?? 'dat';
  await download(
    '/v1/exports/plan',
    {
      format,
      name: input.name || 'District plan',
      district_count: input.districtCount,
      assignments: oneBased,
      labels: input.labels,
      colors: input.colors,
      comments: input.comments || null,
      basemap: useBasemapStore.getState().choice,
    },
    `district-plan.${ext}`,
  );
}

export async function exportCoi(format: ExportFormat, input: CoiExportInput): Promise<void> {
  const ext = EXPORT_FORMATS.find((f) => f.format === format)?.ext ?? 'dat';
  await download(
    '/v1/exports/coi',
    {
      format,
      title: input.title || null,
      narrative: input.narrative,
      interests: input.interests || null,
      polygon_geojson: input.polygon,
      basemap: useBasemapStore.getState().choice,
    },
    `community.${ext}`,
  );
}
